import { Box, Stack, SxProps, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Theme } from '@mui/material/styles/createTheme';
import { GridSize, ResponsiveStyleValue } from '@mui/system';
import { lazy, Suspense, useCallback, useState } from 'react';
import { DynamicIcon, NamedLayoutBlock } from 'src/components';
import { config } from 'src/core/config';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { useScreenDimensions } from 'src/utils/system';
import { DtoolsDataImportItem } from './d-tools/dtools-data-import-item';
import { DataImportItem } from './data-import-item';
import { PortalioDataImportItem } from './portalio/portalio-data-import-item';

const FlatfileApp = lazy(() => import('./flat-file/flatfile.view'));

export const DataImport = () => {
  const [showFlatFileImport, setShowFlatFileImport] = useState(false);
  const theme = useTheme();
  const { checkValidity, loading: loadingGroupRules } = useGroupRules();
  const allowFlatFileDataImport =
    !loadingGroupRules && checkValidity([UserGroupRules.AccessToFlatFileDataImport]);

  const onFlatFileImportClick = useCallback(() => {
    setShowFlatFileImport(true);
  }, []);

  const navbar = useGlobalStore.use.navbar();
  const { isMobile } = useScreenDimensions();

  if (showFlatFileImport) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <FlatfileApp
          onClose={() => setShowFlatFileImport(false)}
          publishableKey={config.flatfileToken}
        />
      </Suspense>
    );
  }

  const itemGridSize: ResponsiveStyleValue<GridSize> = {
    xs: 12,
    sm: navbar.isOpen ? 12 : 6,
    md: 4,
    lg: 2,
  };

  const itemGridSx: SxProps<Theme> = {
    minWidth: !isMobile ? '350px' : undefined,
  };

  return (
    <NamedLayoutBlock
      title="Data Import"
      sx={{
        flex: 1,
        display: { sm: 'flex' },
      }}
    >
      <Stack spacing={2} sx={{ overflow: 'hidden', height: '100vh' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto', margin: '1rem 0' }}>
          <Grid container spacing={4} sx={{ margin: theme.spacing(2) }}>
            {allowFlatFileDataImport ? (
              <Grid size={itemGridSize} sx={itemGridSx}>
                <DataImportItem
                  onClick={onFlatFileImportClick}
                  title="CSV Import"
                  actionButtonText="Start Import"
                  description="Import from a CSV file using Flatfile."
                  icon={
                    <DynamicIcon
                      name="upload"
                      library="lucide"
                      size={24}
                      sx={{ color: theme.palette.custom.tertiary.tertiary60 }}
                    />
                  }
                />
              </Grid>
            ) : null}
            <DtoolsDataImportItem size={itemGridSize} sx={itemGridSx} />
            <PortalioDataImportItem size={itemGridSize} sx={itemGridSx} />
          </Grid>
        </Box>
      </Stack>
    </NamedLayoutBlock>
  );
};
